import { BarChart, LineChart, PieChart } from "@mui/x-charts";
import axios from "axios";
import moment, { Moment } from "moment";
import React, { useEffect, useState } from "react";
import AppBar from "src/components/AppBar/AppBar";
import { Container } from "src/components/Container/Container";
import { SimpleDatePicker } from "src/components/Form/SimpleDatePickerFieldV2/SimpleDatePickerField";
import GuardedComponent from "src/components/GuardedComponent/GuardedComponent";
import Icon, { IconName } from "src/components/Icon/Icon";
import FilterContainer from "src/components/TableV2/FilterContainer";
import Title from "src/components/TableV2/Title";
import { UserRole } from "src/store/models/user.model";
import { openUrl } from "src/util/util.navigation";
import * as S from "../../constants/StringConstants";
import "./Dashboard.scss";

interface MetricsData {
  date: string;
  blocks: number;
  bounceDrops: number;
  bounces: number;
  clicks: number;
  deferred: number;
  delivered: number;
  invalidEmails: number;
  opens: number;
  processed: number;
  requests: number;
  spamReportDrops: number;
  spamReports: number;
  uniqueClicks: number;
  uniqueOpens: number;
  unsubscribeDrops: number;
  unsubscribes: number;
  noOfLandingPageSubmissions: number;
}

// Fetch monthly aggregated data
const fetchMonthlyEmailStats = async () => {
  try {
    const res = await axios.get("/ioapp/emailStats/monthly");
    return res.data;
  } catch (error) {}
};

// Fetch data for a custom date range
const fetchEmailStatsByRange = async (startDate: string, endDate: string) => {
  try {
    const res = await axios.get(`/ioapp/emailStats/byRange?startDate=${startDate}&endDate=${endDate}`);
    return res.data;
  } catch (error) {}
};

const Dashboard: React.FC = () => {
  const [data, setData] = useState<MetricsData[]>([]);
  const [startDate, setStartDate] = useState<Moment | null>(moment("2024-08-01"));
  const [endDate, setEndDate] = useState<Moment | null>(moment("2024-10-01"));
  const [_, setIsCustomRange] = useState(false);
  const [loading, setLoading] = useState(false);

  // Fetch monthly data on initial load
  useEffect(() => {
    const fetchMonthlyData = async () => {
      setLoading(true);
      try {
        const fetchedData = await fetchMonthlyEmailStats();
        setData(fetchedData);
      } catch (error) {
      } finally {
        setLoading(false);
      }
    };

    fetchMonthlyData();
  }, []);

  // Fetch data based on selected date range
  const fetchCustomRangeData = async () => {
    if (startDate && endDate) {
      setIsCustomRange(true); // Marking to indicate using custom range data
      setLoading(true);
      try {
        const fetchedData = await fetchEmailStatsByRange(startDate.format("YYYY-MM-DD"), endDate.format("YYYY-MM-DD"));
        setData(fetchedData);
      } catch (error) {
      } finally {
        setLoading(false);
      }
    }
  };

  return (
    <div className="table-view-v2__content">
      <div className="table-view__header-buttons-container">
        <AppBar icon={IconName.Dashboard} title={S.DASH_PAGE_TITLE} loading={loading}>
          <div className="table-view__buttons-container">
            <GuardedComponent
              authorizedRoles={[
                UserRole.Admin,
                UserRole.ProposalSpecialist,
                UserRole.RegionalBranchManager,
                UserRole.BranchManager,
                UserRole.UnderwritingAssociate,
              ]}>
              <button className="side-nav__add-button" onClick={() => openUrl("/proposals/new/details")}>
                <Icon name={IconName.Plus} />
                {S.NEW_PROPOSAL}
              </button>
            </GuardedComponent>
          </div>
        </AppBar>
      </div>

      <div className="table-container-v2">
        <div className="table-toolbar__wrap">
          <FilterContainer
            left={<Title text={S.DASH_PAGE_TITLE} />}
            right={
              <>
                {/* Date Range Selection */}
                <div className="dashboard-date-pickers">
                  <SimpleDatePicker
                    label={S.DASH_START_DATE}
                    name="startDate"
                    input={{
                      name: "startDate",
                      value: startDate ? startDate.toDate() : null,
                      onChange: (val: Date | null) => setStartDate(val ? moment(val) : null),
                      onBlur: () => {},
                      onFocus: () => {},
                    }}
                    meta={{ touched: false, error: null }}
                    bare
                  />{" "}
                </div>

                <div className="dashboard-date-pickers">
                  <SimpleDatePicker
                    label={S.DASH_END_DATE}
                    name="endDate"
                    input={{
                      name: "endDate",
                      value: endDate ? endDate.toDate() : null,
                      onChange: (val: Date | null) => setEndDate(val ? moment(val) : null),
                      onBlur: () => {},
                      onFocus: () => {},
                    }}
                    meta={{ touched: false, error: null }}
                    bare
                  />
                </div>

                <button type="button" className="button-v2 button-v2__primary small" onClick={fetchCustomRangeData}>
                  {S.DASH_APPLY}
                </button>

                <button
                  className="button-v2 gray small"
                  onClick={async () => {
                    setLoading(true);
                    const monthlyData = await fetchMonthlyEmailStats();
                    setData(monthlyData);
                    setIsCustomRange(false); // Reset to monthly data
                    setLoading(false);
                  }}>
                  {S.DASH_RESET}
                </button>
              </>
            }></FilterContainer>
        </div>
      </div>

      {/* Delivered, Deferred, and Blocks */}
      <Container>
        <div className="report__col report__title full">{S.DASH_DELIVERED_VS_DEFERRED_VS_BLOCKS}</div>
        <div className="report__col report__description full">
          <BarChart
            width={1000}
            height={400}
            series={[
              { data: data.map(d => d.delivered), label: S.DASH_DELIVERED, color: "#4CAF50" },
              { data: data.map(d => d.deferred), label: S.DASH_DEFERRED, color: "#FF9800" },
              { data: data.map(d => d.blocks), label: S.DASH_BLOCKS, color: "#F44336" },
            ]}
            xAxis={[{ scaleType: "band", data: data.map(d => d.date) }]}
          />
        </div>
      </Container>

      {/* Engagement Breakdown */}
      <Container>
        <div className="report__col report__title full">{S.DASH_ENGAGEMENT_BREAKDOWN}</div>
        <div className="report__col report__description full">
          <PieChart
            width={1000}
            height={400}
            series={[
              {
                data: [
                  { value: data.reduce((acc, item) => acc + item.opens, 0), label: S.DASH_OPENS },
                  { value: data.reduce((acc, item) => acc + item.clicks, 0), label: S.DASH_CLICKS },
                  { value: data.reduce((acc, item) => acc + item.unsubscribes, 0), label: S.DASH_UNSUBSCRIBES },
                  { value: data.reduce((acc, item) => acc + item.spamReports, 0), label: S.DASH_SPAM_REPORTS },
                ],
              },
            ]}
          />
        </div>
      </Container>

      {/* Monthly or Custom Range Trends in Opens, Clicks, Unsubscribes */}
      <Container>
        <div className="report__col report__title full">{S.DASH_MONTHLY_ENGAGEMENT_TRENDS}</div>
        <div className="report__col report__description full">
          <LineChart
            width={1000}
            height={400}
            series={[
              { data: data.map(d => d.opens), label: S.DASH_OPENS, color: "#3f51b5" },
              { data: data.map(d => d.clicks), label: S.DASH_CLICKS, color: "#9C27B0" },
              { data: data.map(d => d.unsubscribes), label: S.DASH_UNSUBSCRIBES, color: "#FFC107" },
            ]}
            xAxis={[{ scaleType: "band", data: data.map(d => d.date) }]}
          />
        </div>
      </Container>

      {/* Bounces and Deferred Emails */}
      <Container>
        <div className="report__col report__title full">{S.DASH_BOUNCES_AND_DEFERRED_EMAILS}</div>
        <div className="report__col report__description full">
          <LineChart
            width={1000}
            height={400}
            series={[
              { data: data.map(d => d.bounces), label: S.DASH_BOUNCES, color: "#FF5722" },
              { data: data.map(d => d.deferred), label: S.DASH_DEFERRED, color: "#009688" },
            ]}
            xAxis={[{ scaleType: "band", data: data.map(d => d.date) }]}
          />
        </div>
      </Container>

      <Container>
        <div className="report__col report__title full">{S.DASH_LANDING_PAGE_SUBMISSIONS}</div>
        <div className="report__col report__description full">
          <BarChart
            width={1000}
            height={400}
            series={[
              {
                data: data.map(d => d.noOfLandingPageSubmissions),
                label: S.DASH_LANDING_PAGE_SUBMISSIONS,
                color: "#FF5722",
              },
            ]}
            xAxis={[{ scaleType: "band", data: data.map(d => d.date) }]}
          />
        </div>
      </Container>
    </div>
  );
};

export default Dashboard;
